//
//
// witRBAC - higher-order function for components that require certain permissions//
// usage - useRBAC(SuperOrganizationRoleCodes.TeamCreator)(Component)
//
//
import useAccountInfo from 'hooks/account/useAccountInfo';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectActiveSuperOrganizationRole } from '../../slices/superOrganizationAdminSlice';
import { isSuperOrgPlanFree } from '../../utils/helper';
import { SuperOrganizationRoleCodes } from '../../utils/permissionHelpers';

export const hasSystemAdminRole = (activeSuperOrganizationRoleCode) => {
  if (!activeSuperOrganizationRoleCode) {
    return false;
  }
  return hasRoleInActiveSuperOrganization(activeSuperOrganizationRoleCode, SuperOrganizationRoleCodes.SystemAdmin);
};

export const hasRoleInActiveSuperOrganization = (presentRole, requiredRoleCode) => {
  let presentRoleCode = presentRole?.code;

  if (presentRoleCode === undefined || presentRoleCode === null) {
    presentRoleCode = 0;
  }
  return presentRoleCode >= requiredRoleCode;
};

export const withRBAC = (requiredRoleCode) => (Component) => (props) => {
  const [allowed, setAllowed] = useState(false);
  const activeSuperOrganizationRole = useSelector(selectActiveSuperOrganizationRole);

  if (requiredRoleCode === undefined || requiredRoleCode === null) {
    requiredRoleCode = 0;
  }

  useEffect(() => {
    setAllowed(hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, requiredRoleCode));
  }, [activeSuperOrganizationRole, requiredRoleCode]);

  return allowed ? <Component {...props} /> : null;
};

export const useRBACPermissions = () => {
  const activeSuperOrganizationRole = useSelector(selectActiveSuperOrganizationRole);
  const { data: account } = useAccountInfo();
  const activeTeam = account?.active_organization;

  return useMemo(() => {
    const canCreateTeam = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.TeamCreator);
    const canProvisionTeam = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.TeamCreator);
    const canDeprovisionTeam = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.TeamCreator);
    const canManageTeamUsers = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.TeamCreator);
    const canInviteTeammates = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.ScribeCreator);
    const canRemoveUserOrInviteFromTeam = hasRoleInActiveSuperOrganization(
      activeSuperOrganizationRole,
      SuperOrganizationRoleCodes.SystemAdmin
    );
    const canChangeUserPermissionsInTeam = hasRoleInActiveSuperOrganization(
      activeSuperOrganizationRole,
      SuperOrganizationRoleCodes.SystemAdmin
    );
    const canViewUsers = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.ScribeCreator);
    const canCreateRole = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.SystemAdmin);
    const canManageRoles = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.SystemAdmin);
    const canSeeAdminLink = hasRoleInActiveSuperOrganization(activeSuperOrganizationRole, SuperOrganizationRoleCodes.SystemAdmin);
    const isSystemAdmin = hasSystemAdminRole(activeSuperOrganizationRole);
    const isAdminOfActiveTeam = activeTeam?.permission_level === 'admin';
    const isAdmin = isSystemAdmin || isAdminOfActiveTeam;
    const isLoggedIn = !!account;
    const isFreeUser = isSuperOrgPlanFree(account?.active_organization?.super_organization);
    const canUseOpenAI = activeTeam?.super_organization?.allow_openai;

    const canCreateDocumentsOrFolders = activeTeam?.permission_level ? activeTeam?.permission_level !== 'read' : true;
    const canChangeOrDeleteDocumentsOrFolders = canCreateDocumentsOrFolders;

    return {
      canCreateDocumentsOrFolders,
      canChangeOrDeleteDocumentsOrFolders,
      canCreateTeam,
      canProvisionTeam,
      canDeprovisionTeam,
      canManageTeamUsers,
      canRemoveUserOrInviteFromTeam,
      canChangeUserPermissionsInTeam,
      canSeeAdminLink,
      canInviteTeammates,
      canSeeBillingLink: canSeeAdminLink,
      canSeeTeamSettings: isAdmin,
      isAdmin,
      isAdminOfActiveTeam,
      canViewUsers,
      canCreateRole,
      canManageRoles,
      isSystemAdmin,
      isLoggedIn,
      isFreeUser,
      canUseOpenAI
    };
  }, [activeSuperOrganizationRole, account?.organizations, activeTeam?.id]);
};

export { SuperOrganizationRoleCodes };
